<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-10">
      <h4 class="mb-5 hidden">
        Let’s start your virtual job experience in the Creative Industries!
      </h4>
      <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Description</u></heading>
      <p class="mb-5">
        Apart from obvious roles such as solicitors, barristers, and judges, there is a wide range
        of other lawyer and non-lawyer career paths you can take within this sector, from legal
        journalists, legal secretaries, typists, human resources personnel, paralegals, and legal
        executives to solicitor advocates, coroners, lecturers, and court clerks.
      </p>
      <p class="mb-5">
        There’s so much variety, and there’s certainly something for everyone; whether you go on to
        graduate from university or not, the law provides you with a wealth of options.
      </p>
      <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Education</u></heading>
      <p class="mb-5">
        With such a wide variety of careers and jobs available in this sector, it should be no
        surprise that there are a correspondingly wide array of educational requirements. In the
        next section, we’ll look at several representative jobs in law and find out a little more
        about entry requirements and some important details such as salaries and growth prospects.
      </p>
      <p class="mb-5">
        Even if you don’t see a job in the legal field that you are interested in below, the
        activities in this virtual job experience will be useful, regardless of what role in the
        legal sector you are interested in. It will also be worth checking out some of the jobs
        below, as you might just discover a new area of interest you hadn’t yet thought of.
      </p>
      <p class="mb-5">
        Use the tabs below to further explore the job/career(s) that you might be interested in.
      </p>
    </vx-card>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Legal Secretary">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">What they do:</h4>
            <p class="mb-5">
              Legal Secretaries perform secretarial, clerical, and other administrative tasks
              supporting Legal Professionals, applying knowledge of legal terminology, procedures,
              and documents.
            </p>
            <h4 class="mb-5">What education they need:</h4>
            <p class="mb-5">
              You can work as a Legal Secretary without formal qualifications. However, a
              Certificate III, IV, or diploma in legal services or secretarial and clerical studies
              may be useful.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Court & Legal Clerk">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">What they do:</h4>
            <p class="mb-5">
              Court and Legal Clerks provide administrative and operational support to Legal
              Professionals by performing clerical work associated with courts’ functions, legal
              practices, and the administration of trusts and estates.
            </p>
            <h4 class="mb-5">What education they need:</h4>
            <p class="mb-5">
              You can work as a Court or Legal Clerk without formal qualifications. However, a
              course in legal services, law, justice, criminology, or another related field may be
              useful. University and Vocational Education and Training (VET) are both common study
              pathways.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Conveyancer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">What they do:</h4>
            <p class="mb-5">
              Conveyancers and Legal Executives act for and on behalf of clients in the areas of
              property transfer, company and business law, trusts, wills, probate, and litigation.
            </p>
            <h4 class="mb-5">What education they need:</h4>
            <p class="mb-5">
              You need extensive experience or a formal qualification in conveyancing, law, legal
              studies, or another related field to work as a Conveyancer or Legal Executive.
              University and Vocational Education and Training (VET) are both common study pathways.
            </p>
            <p class="mb-5">Registration or licencing may be required.</p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="http://compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses, providers, and student outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.aapathways.com.au/" target="_blank">AAPathways</a> website to
              explore VET training pathways.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Solicitor">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">What they do:</h4>
            <p class="mb-5">
              Solicitors provide legal advice, prepare and draft legal documents, and conduct
              negotiations on behalf of clients on matters associated with the law.
            </p>
            <h4 class="mb-5">What education they need:</h4>
            <p class="mb-5">
              You need a bachelor’s degree in law to work as a Solicitor. It is also common to
              complete postgraduate studies.
            </p>
            <p class="mb-5">Registration or licencing may be required.</p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="http://compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Barrister">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">What they do:</h4>
            <p class="mb-5">
              Barristers plead cases before civil, criminal, and industrial courts and other
              tribunals.
            </p>
            <p class="mb-5">Specialisations: Queen's Counsel, Senior Counsel.</p>
            <h4 class="mb-5">What education they need:</h4>
            <p class="mb-5">
              To work as a Barrister, you need to complete a law degree, take a Bar Examination and
              obtain an Australian Practising Certificate (Barrister).
            </p>
            <p class="mb-5">Registration or licencing may be required.</p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="http://compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <h4 class="mb-5">Quick Details:</h4>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Legal Secretary">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <h4 class="mb-5">Legal Secretaries</h4>
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a small occupation.</li>
                  <li>
                    <strong>Full-time:</strong> Many work full-time (62%, similar to the average of
                    66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 39 hours per week at work
                    (compared to the average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 41 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender:</strong> 99% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>

                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Liaises with other staff to arrange meetings and to gain and provide
                    information.
                  </li>
                  <li>
                    Prepares reports, briefing notes, and correspondence and proofreads work for
                    typographical and grammatical errors.
                  </li>
                  <li>Maintains diaries and makes travel arrangements.</li>
                  <li>
                    Processes incoming and outgoing mail, filing correspondence, and maintains
                    records.
                  </li>
                  <li>Answers telephone calls, responds to inquiries, and redirects callers.</li>
                  <li>Takes and transcribes dictation of letters and other documents.</li>
                  <li>
                    Greets visitors, ascertains the nature of business and directs visitors to
                    appropriate personnel.
                  </li>
                  <li>May implement management decisions and maintain records of meetings.</li>
                </ul>

                <h4 class="mb-5">Useful Resources:</h4>
                <p class="mb-5">
                  Before starting a course, check it will provide you with the skills and
                  qualifications you need. Visit:
                </p>
                <p class="mb-5">
                  <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
                  Vocational Education and Training (VET) courses, providers, and student outcomes.
                </p>
                <p class="mb-5">
                  <a href="https://www.aapathways.com.au/" target="_blank">AAPathways</a> website to
                  explore VET training pathways.
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/law/jo1.png"
                  class="img-responsive"
                  alt="Secretaries job outlook: $1,146 weekly pay, declining future growth. Legal Secretaries: 9,100 workers, high skill level, 62% full-time, 39 hours average full-time week, average age 41, 99% female."
                />
                <p class="mt-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Court & Legal Clerk">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <h4 class="mb-5">Court & Legal Clerks</h4>
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a medium-sized occupation.</li>
                  <li>
                    <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,119
                    per week (below the average of $1,460). Earnings tend to be lower when starting
                    out and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time:</strong> Many work full-time (65%, similar to the average of
                    66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 40 hours per week at work
                    (compared to the average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 35 years (compared to the average of 40
                    years). Many workers are under 25 years of age (23%).
                  </li>
                  <li>
                    <strong>Gender:</strong> 75% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>

                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Listing actions for hearing and processing documentation for court actions
                  </li>
                  <li>Documenting details of court proceedings, actions, and decisions</li>
                  <li>
                    Enforcing the law as an officer of the court by executing court orders such as
                    eviction notices
                  </li>
                  <li>Serving legal orders and documents such as summonses and subpoenas</li>
                  <li>
                    Organising jury and witness lists, and summonsing and swearing in juries and
                    witnesses
                  </li>
                  <li>Maintaining order in court and hearing rooms and adjacent areas</li>
                  <li>
                    Assisting Solicitors in areas of conveyancing, contracts, common law, probate,
                    and other legal practice matters
                  </li>
                  <li>
                    Satisfying statutory requirements, establishing beneficial entitlements, and
                    distributing assets
                  </li>
                  <li>
                    Maintaining probate and trust files, investing trust funds, and administering
                    accounts
                  </li>
                </ul>
                <h4 class="mb-5">Useful Resources:</h4>
                <p class="mb-5">
                  Before starting a course, check it will provide you with the skills and
                  qualifications you need. Visit:
                </p>
                <p class="mb-5">
                  <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to
                  search and compare higher education courses.
                </p>
                <p class="mb-5">
                  <a href="http://compared.edu.au/" target="_blank">ComparED</a> to compare
                  undergraduate and postgraduate student experiences and outcomes.
                </p>
                <p class="mb-5">
                  <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
                  Vocational Education and Training (VET) courses, providers, and student outcomes.
                </p>
                <p class="mb-5">
                  <a href="https://www.aapathways.com.au/" target="_blank">AAPathways</a> website to
                  explore VET training pathways.
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/law/jo2.png"
                  alt="Court and Legal Clerks job outlook: $1,119 weekly pay, strong future growth, 16,800 workers, medium skill level, 65% full-time, 40 hours average full-time week, average age 35, 75% female."
                  class="img-responsive"
                />
                <p class="mt-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Conveyancer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <h4 class="mb-5">Conveyancers & Legal Executives</h4>
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a medium-sized occupation.</li>
                  <li>
                    <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,249
                    per week (below the average of $1,460). Earnings tend to be lower when starting
                    out and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time:</strong> Many work full-time (62%, similar to the average of
                    66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 41 hours per week at work
                    (compared to the average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 35 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender:</strong> 82% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>

                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Preparing, examining, and advising on contracts of sale for properties and
                    businesses
                  </li>
                  <li>
                    Carrying out title searches and contacting government authorities to find out if
                    any planned development, illegal building work, and disputes could affect
                    properties and businesses
                  </li>
                  <li>Preparing, examining, and advising on mortgage documentation</li>
                  <li>
                    Negotiating the terms and conditions of, and exchanging, contracts of sale,
                    paying deposits, arranging payment of stamp duty, checking for outstanding
                    arrears and land tax obligations, and calculating adjustments for council and
                    water rates
                  </li>
                  <li>
                    Interviewing clients to determine the nature of issues and receiving written
                    information concerning cases from Legal Professionals and clients
                  </li>
                  <li>
                    Conferring with clients and potential witnesses and drawing up statements and
                    proposed affidavits in preparation for court proceedings
                  </li>
                  <li>Maintaining legal files</li>
                  <li>Preparing, analysing, and interpreting a variety of legal documents</li>
                  <li>
                    Assisting Legal Professionals to prepare cases for court by conducting
                    investigations, undertaking research, arranging witness preparation and
                    attendance, and preparing and filing court documents
                  </li>
                  <li>
                    May supervise Law Clerks and Legal Secretaries and take responsibility for their
                    work
                  </li>
                </ul>
                <h4 class="mb-5">Useful Resources:</h4>
                <p class="mb-5">
                  <a href="https://www.aicnational.com.au/" target="_blank"
                    >Australian Institute of Conveyancers</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.lawcouncil.asn.au/" target="_blank"
                    >Law Council of Australia</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.liv.asn.au/LegalExecutives" target="_blank"
                    >Institute of Legal Executives, Victoria</a
                  >
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/law/jo3.png"
                  alt="Conveyancers and Legal Executives job outlook: $1,249 weekly pay, stable future growth, 15,200 workers, high skill level, 62% full-time, 41 hours average full-time week, average age 35, 82% female."
                  class="img-responsive"
                />
                <p class="mt-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Solicitor">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <h4 class="mb-5">Solicitors</h4>
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a very large occupation.</li>
                  <li>
                    <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,646
                    per week (very high compared to the average of $1,460). Earnings tend to be
                    lower when starting out and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time:</strong> Most work full-time (84%, much higher than the
                    average of 66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 48 hours per week at work
                    (compared to the average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 39 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender:</strong> 51% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>

                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Interviewing clients to determine the nature of problems and recommending and
                    undertaking appropriate legal action
                  </li>
                  <li>
                    Preparing cases for court by conducting investigations, undertaking research,
                    arranging witness preparation and attendance, and giving notice of court actions
                  </li>
                  <li>Representing clients in court</li>
                  <li>
                    Managing conveyancing and other property matters by preparing contracts of sale,
                    mortgage documents, lease documents, and other documents relating to the
                    transfer of land and buildings
                  </li>
                  <li>Preparing and critically reviewing contracts between parties</li>
                  <li>Preparing wills</li>
                  <li>
                    Providing advice on family law, company law, partnerships, commercial law, and
                    trusts
                  </li>
                  <li>May act as trustee or guardian</li>
                  <li>May act as executor of clients' wills</li>
                </ul>
                <h4 class="mb-5">Useful Resources:</h4>
                <p class="mb-5">
                  <a href="https://www.lawcouncil.asn.au/" target="_blank"
                    >Law Council of Australia</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.lawsociety.com.au/" target="_blank"
                    >The Law Society of NSW</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.liv.asn.au/" target="_blank">Law Institute of Victoria</a>
                </p>
                <p class="mb-5">
                  <a href="https://www.qls.com.au/Home" target="_blank">Queensland Law Society</a>
                </p>
                <p class="mb-5">
                  <a href="https://www.lawsocietysa.asn.au/" target="_blank"
                    >Law Society of South Australia</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.lawsocietywa.asn.au/" target="_blank"
                    >Law Society of Western Australia</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://lst.org.au/" target="_blank">Law Society of Tasmania</a>
                </p>
                <p class="mb-5">
                  <a href="https://lawsocietynt.asn.au/" target="_blank"
                    >Law Society Northern Territory</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.actlawsociety.asn.au/" target="_blank"
                    >Law Society of the ACT</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://docs.jobs.gov.au/node/33319" target="_blank"
                    >Solicitor labour market research reports</a
                  >
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/law/jo4.png"
                  alt="Solicitors job outlook: $1,646 weekly pay, very strong future growth, 79,300 workers, very high skill level, 84% full-time, 48 hours average full-time week, average age 39, 51% female."
                  class="img-responsive"
                />
                <p class="mt-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Barrister">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <h4 class="mb-5">Barristers</h4>
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a medium-sized occupation.</li>
                  <li>
                    <strong>Full-time:</strong> Most work full-time (85%, much higher than the
                    average of 66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 50 hours per week at work
                    (compared to the average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 46 years (compared to the average of 40
                    years). Many workers are 45 years or older (56%).
                  </li>
                  <li>
                    <strong>Gender:</strong> 32% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>

                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Receiving written information in the form of briefs and verbal instructions
                    concerning cases from Solicitors, other specialist Legal Professionals, and
                    clients
                  </li>
                  <li>Providing advice and written opinions on points of law</li>
                  <li>
                    Conferring with clients and witnesses in preparation for court proceedings
                  </li>
                  <li>Drawing up pleadings, affidavits, and other court documents</li>
                  <li>Researching statutes and previous court decisions relevant to cases</li>
                  <li>
                    Outlining the facts to the court, calling and questioning witnesses, and making
                    addresses to the court to argue a client's case
                  </li>
                  <li>Providing an opinion on complex legal issues</li>
                  <li>May draw up or settle documents</li>
                </ul>
                <h4 class="mb-5">Useful Resources:</h4>

                <p class="mb-5">
                  <a href="https://austbar.asn.au/" target="_blank">Australian Bar Association</a>
                </p>
                <p class="mb-5">
                  <a href="https://www.lawcouncil.asn.au/" target="_blank"
                    >The Law Council of Australia</a
                  >
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/law/jo5.png"
                  alt="Barristers job outlook: weekly pay unavailable, very strong future growth, 9,000 workers, very high skill level, 85% full-time, 50 hours average full-time week, average age 46, 32% female."
                  class="img-responsive"
                />
                <p class="mt-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>
  </div>
</template>

<script>
export default {
  data() {
    return {
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      activeTab: 0,
    };
  },
};
</script>
